import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import "./styles/BlogList.css";
import "../styles/global.css";

const ITEMS_PER_PAGE = 9;

/* ── Colored SVG illustration per card ── */
const BlogIllustration = ({ color = "#0d6efd", title = "" }) => {
  const initials = title.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
  const uid = `g${initials}${color.replace("#", "")}`;
  return (
    <svg viewBox="0 0 480 270" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
      <defs>
        <linearGradient id={uid} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <rect width="480" height="270" fill={`url(#${uid})`} />
      <circle cx="420" cy="40" r="90" fill={color} fillOpacity="0.08" />
      <circle cx="60" cy="240" r="70" fill={color} fillOpacity="0.06" />
      <rect x="130" y="78" width="220" height="4" rx="2" fill={color} fillOpacity="0.2" />
      <rect x="150" y="94" width="180" height="4" rx="2" fill={color} fillOpacity="0.15" />
      <rect x="170" y="110" width="140" height="4" rx="2" fill={color} fillOpacity="0.1" />
      <circle cx="240" cy="148" r="32" fill={color} fillOpacity="0.12" />
      <text x="240" y="156" textAnchor="middle" fontSize="20" fontWeight="bold" fill={color} fillOpacity="0.75" fontFamily="sans-serif">{initials}</text>
    </svg>
  );
};

/* ── Skeleton card ── */
const SkeletonCard = () => (
  <li className="bl-skeleton">
    <div className="sk-img" />
    <div className="sk-body">
      <div className="sk-line short" /><div className="sk-line title" />
      <div className="sk-line full" /><div className="sk-line medium" /><div className="sk-line short" />
    </div>
  </li>
);

const CARD_COLORS = ["#0d6efd", "#198754", "#dc3545", "#fd7e14", "#6f42c1"];

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
const BlogList = () => {
  const [searchName, setSearchName]   = useState("");
  const [selectedCat, setSelectedCat] = useState("");
  const [sortBy, setSortBy]           = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterOpen, setFilterOpen]   = useState(false);

  const [blogs, setBlogs]           = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(false);

  const token = localStorage.getItem("authToken");

  useEffect(() => { fetchBlogs(); }, []);

  const fetchBlogs = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/blogs_front`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          const formattedBlogs = data.data.map((item, index) => ({
            id:            item.id,
            slug:          item.slug,
            title:         item.name,
            excerpt:       item.short_desc,
            category_name: item.category,
            category_id:   item.category,
            // ✅ Use author_name from API; fallback to "Admin"
            author_name:   item.author_name || "Admin",
            published_at:  item.created_at,
            read_time:     5,
            color:         CARD_COLORS[index % CARD_COLORS.length],
            image:         item.blogimg
              ? `${API_BASE_URL}public/blog/${item.blogimg}`
              : null,
          }));

          setBlogs(formattedBlogs);

          const uniqueCategories = [...new Set(formattedBlogs.map((b) => b.category_name))];
          setCategories(
            uniqueCategories.map((cat) => ({
              id:    cat,
              name:  cat,
              count: formattedBlogs.filter((b) => b.category_name === cat).length,
            }))
          );
        }
      })
      .finally(() => setLoading(false));
  };

  const filtered = useMemo(() => {
    let list = [...blogs];
    if (selectedCat) {
      list = list.filter((b) => b.category_id?.toLowerCase() === selectedCat?.toLowerCase());
    }
    if (searchName.trim()) {
      const q = searchName.toLowerCase();
      list = list.filter(b =>
        b.title.toLowerCase().includes(q) ||
        (b.excerpt || "").toLowerCase().includes(q) ||
        (b.author_name || "").toLowerCase().includes(q)
      );
    }
    if (sortBy === "az")     list.sort((a, b) => a.title.localeCompare(b.title));
    if (sortBy === "za")     list.sort((a, b) => b.title.localeCompare(a.title));
    if (sortBy === "oldest") list.sort((a, b) => new Date(a.published_at) - new Date(b.published_at));
    return list;
  }, [blogs, searchName, selectedCat, sortBy]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const safePage   = Math.min(currentPage, totalPages || 1);
  const paginated  = filtered.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  const handleCatChange = (cat) => { setSelectedCat(cat); setCurrentPage(1); setFilterOpen(false); };
  const handleSort      = (e)   => { setSortBy(e.target.value); setCurrentPage(1); };
  const handleReset     = ()    => { setSelectedCat(""); setSearchName(""); setSortBy(""); setCurrentPage(1); setFilterOpen(false); };

  return (
    <div className="bl-page">
      <Header />

      {/* Hero */}
      <section className="bl-hero">
        <div className="container-fluid px-lg-5">
          <h1 className="bl-hero-title">Health & Wellness Blog</h1>
          <p className="bl-hero-sub">Expert advice, health tips, and the latest medical insights — written by doctors and specialists.</p>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="bl-bc">
        <div className="container-fluid px-lg-5">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item active" aria-current="page">Blog</li>
            </ol>
          </nav>
        </div>
      </section>

      {/* Listing */}
      <section className="bl-section">
        <div className="container-fluid px-lg-5">
          <div className="bl-layout">

            {/* Sidebar */}
            <aside className="bl-sidebar">
              <div className="bl-sb-head">
                <span className="bl-sb-head-title">Filters</span>
                <button className="bl-sb-clear" onClick={handleReset}>Clear All</button>
              </div>
              <div className="bl-sb-sec-title">Categories</div>
              <div className="bl-cat-list">
                <button className={`bl-cat-item${selectedCat === "" ? " ia" : ""}`} onClick={() => handleCatChange("")}>
                  <span>All Articles</span><span className="bl-cat-cnt">{blogs.length}</span>
                </button>
                {categories.map(cat => (
                  <button key={cat.id} className={`bl-cat-item${selectedCat === cat.id ? " ia" : ""}`} onClick={() => handleCatChange(cat.id)}>
                    <span>{cat.name}</span><span className="bl-cat-cnt">{cat.count}</span>
                  </button>
                ))}
              </div>
              <div className="bl-sb-sec-title" style={{ borderTop: "1px solid #dee2e6", paddingTop: 14 }}>Quick Stats</div>
              <div className="bl-stats">
                {[
                  { icon: "fa-newspaper",   val: blogs.length,      label: "Total Articles" },
                  { icon: "fa-folder",      val: categories.length, label: "Categories" },
                  { icon: "fa-user-doctor", val: 5,                 label: "Expert Authors" },
                ].map(s => (
                  <div key={s.label} className="bl-stat">
                    <div className="bl-stat-ic"><i className={`fa-solid ${s.icon}`} /></div>
                    <span><span className="bl-stat-v">{s.val}</span> {s.label}</span>
                  </div>
                ))}
              </div>
            </aside>

            {/* Main */}
            <div>
              <div className="bl-sb-bar">
                <div className="bl-sw">
                  <i className="fa-solid fa-search bl-si" />
                  <input
                    type="text"
                    placeholder="Search articles by title, topic or author…"
                    value={searchName}
                    autoComplete="off"
                    onChange={e => { setSearchName(e.target.value); setCurrentPage(1); }}
                  />
                </div>
              </div>

              <div className="bl-rh">
                <div>
                  <h4 className="bl-rt">
                    {selectedCat ? categories.find(c => String(c.id) === selectedCat)?.name : "Latest Articles"}
                  </h4>
                  <p className="bl-rc">Showing <strong>{filtered.length}</strong> article{filtered.length !== 1 ? "s" : ""}</p>
                </div>
                <div className="d-lg-block d-none">
                  <select className="bl-sort" value={sortBy} onChange={handleSort}>
                    <option value="">Sort By (Latest)</option>
                    <option value="oldest">Oldest First</option>
                    <option value="az">A–Z</option>
                    <option value="za">Z–A</option>
                  </select>
                </div>
              </div>

              {selectedCat && (
                <div>
                  <span className="bl-afp">
                    {categories.find(c => String(c.id) === selectedCat)?.name}
                    <button onClick={() => handleCatChange("")} aria-label="Remove filter">✕</button>
                  </span>
                </div>
              )}

              {loading ? (
                <ul className="bl-grid">
                  {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
                </ul>
              ) : paginated.length > 0 ? (
                <ul className="bl-grid">
                  {paginated.map((blog, idx) => (
                    <li className="bl-card" key={blog.id} style={{ animationDelay: `${idx * 0.06}s` }}>
                      <div className="bl-ci">
                        <Link to={`/blog/${blog.slug}`}>
                          {blog.image ? (
                            <img src={blog.image} alt={blog.title} />
                          ) : (
                            <BlogIllustration color={blog.color} title={blog.title} />
                          )}
                        </Link>
                        <span className="bl-cbadge" style={{ background: blog.color }}>{blog.category_name}</span>
                      </div>
                      <div className="bl-cb">
                        <div className="bl-cm">
                          <span className="bl-mi">
                            <i className="fa-regular fa-calendar" />
                            {new Date(blog.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                          </span>
                        </div>
                        <Link to={`/blog/${blog.slug}`} className="bl-ct">{blog.title}</Link>
                        <p className="bl-ce">{blog.excerpt}</p>
                      </div>
                      <div className="bl-cf">
                        <div className="bl-au">
                          {/* ✅ Author avatar uses first letter of author_name */}
                          <div className="bl-av" style={{ background: blog.color }}>
                            {(blog.author_name || "A").charAt(0).toUpperCase()}
                          </div>
                          <span className="bl-an">{blog.author_name}</span>
                        </div>
                        <Link to={`/blog/${blog.slug}`} className="bl-rm">
                          Read more <i className="fa-solid fa-arrow-right" />
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="bl-empty">
                  <div className="bl-empty-ic"><i className="fa-solid fa-newspaper" /></div>
                  <h5>No Articles Found</h5>
                  <p>Try adjusting your search or category filter.</p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="bl-pag">
                  <button className="bl-pb" disabled={safePage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                    <i className="fa-solid fa-chevron-left" />
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button key={i} className={`bl-pb${safePage === i + 1 ? " ia" : ""}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                  ))}
                  <button className="bl-pb" disabled={safePage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                    <i className="fa-solid fa-chevron-right" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile */}
      <div className="bl-mob">
        <button className="bl-mbb" onClick={() => setFilterOpen(true)}><i className="fa-solid fa-filter" /> Filters</button>
        <button className="bl-mbb pr"><i className="fa-solid fa-up-down" /> Sort By</button>
      </div>
      <div className={`bl-ov${filterOpen ? " vi" : ""}`} onClick={() => setFilterOpen(false)} aria-hidden="true" />
      <div className={`bl-dr${filterOpen ? " op" : ""}`}>
        <div className="bl-drh">
          <h5 className="bl-drt">Filter by Category</h5>
          <button className="bl-drc" onClick={() => setFilterOpen(false)}><i className="fa-solid fa-xmark" /></button>
        </div>
        <div className="bl-drb">
          <label className="bl-dci">
            <input type="radio" name="mc" checked={selectedCat === ""} onChange={() => handleCatChange("")} />
            All Articles ({blogs.length})
          </label>
          {categories.map(cat => (
            <label key={cat.id} className="bl-dci">
              <input type="radio" name="mc" checked={selectedCat === cat.id} onChange={() => handleCatChange(String(cat.id))} />
              {cat.name} ({cat.count})
            </label>
          ))}
        </div>
        <div className="bl-drf">
          <button className="bl-btn-r" onClick={handleReset}>Reset</button>
          <button className="bl-btn-a" onClick={() => setFilterOpen(false)}>Apply</button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogList;